import { useState, useEffect, useCallback } from 'react';
import {
  fetchVisitorStats,
  fetchLiveVisitors,
  fetchProductViews,
  fetchTopViewedProducts,
  type VisitorActivity,
  type VisitorStats,
  type ProductViewRecord,
} from '@/lib/supabase';

type VTab = 'overview' | 'live' | 'pages' | 'customers' | 'products';

const VTABS: { key: VTab; label: string; icon: string; color: string }[] = [
  { key: 'overview',  label: 'Overview',       icon: 'ri-dashboard-3-line',  color: 'bg-slate-800' },
  { key: 'live',      label: 'Live Visitors',  icon: 'ri-pulse-line',        color: 'bg-green-600' },
  { key: 'pages',     label: 'Page Analytics', icon: 'ri-pie-chart-2-line',  color: 'bg-orange-500' },
  { key: 'customers', label: 'Customer Visits', icon: 'ri-user-heart-line',  color: 'bg-purple-600' },
  { key: 'products',  label: 'Product Views',  icon: 'ri-shopping-bag-3-line', color: 'bg-rose-500' },
];

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function pageLabel(page: string): string {
  const map: Record<string, string> = {
    home: 'Home Page',
    category: 'Category / Shop',
    profile: 'Profile',
    product: 'Product Detail',
    order: 'Order Page',
  };
  return map[page] ?? page;
}

function pageIcon(page: string): string {
  const map: Record<string, string> = {
    home: 'ri-home-4-line',
    category: 'ri-store-2-line',
    profile: 'ri-user-line',
    product: 'ri-shopping-bag-line',
    order: 'ri-file-list-3-line',
  };
  return map[page] ?? 'ri-pages-line';
}

function deviceIcon(device: string): string {
  if (device === 'mobile') return 'ri-smartphone-line';
  if (device === 'tablet') return 'ri-tablet-line';
  return 'ri-computer-line';
}

function actionBadge(action: string): { label: string; color: string } {
  const map: Record<string, { label: string; color: string }> = {
    page_view:     { label: 'Viewed',     color: 'bg-slate-100 text-slate-600' },
    add_to_cart:   { label: 'Added Cart', color: 'bg-amber-100 text-amber-700' },
    place_order:   { label: 'Ordered',    color: 'bg-green-100 text-green-700' },
    login:         { label: 'Logged In',  color: 'bg-orange-100 text-orange-700' },
    register:      { label: 'Registered', color: 'bg-purple-100 text-purple-700' },
    search:        { label: 'Searched',   color: 'bg-sky-100 text-sky-700' },
    product_click: { label: 'Product',    color: 'bg-rose-100 text-rose-700' },
  };
  return map[action] ?? { label: action, color: 'bg-gray-100 text-gray-600' };
}

// ─── Hourly Chart ─────────────────────────────────────────────────────────────
function HourlyChart({ data }: { data: Array<{ hour: number; count: number }> }) {
  const max = Math.max(...data.map(d => d.count), 1);
  const peakHour = data.reduce((a, b) => a.count > b.count ? a : b);
  return (
    <div>
      <div className="flex items-end gap-0.5 h-20">
        {data.map(d => (
          <div
            key={d.hour}
            className="flex-1 flex flex-col items-center justify-end group relative"
          >
            <div
              className={`w-full rounded-t-sm transition-all ${d.count > 0 ? 'bg-orange-400 hover:bg-orange-500' : 'bg-gray-100'}`}
              style={{ height: `${Math.max((d.count / max) * 100, d.count > 0 ? 4 : 2)}%` }}
            >
              {d.count > 0 && (
                <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                  {d.hour}:00 — {d.count}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-1 text-[9px] text-gray-400">
        <span>12am</span>
        <span>6am</span>
        <span>12pm</span>
        <span>6pm</span>
        <span>12am</span>
      </div>
      {peakHour.count > 0 && (
        <p className="text-xs text-orange-600 font-semibold mt-1 flex items-center gap-1">
          <i className="ri-fire-line"></i>
          Peak: {peakHour.hour}:00–{peakHour.hour + 1}:00 ({peakHour.count} visits)
        </p>
      )}
    </div>
  );
}

export default function VisitorActivityPanel() {
  const [activeTab, setActiveTab] = useState<VTab>('overview');
  const [stats, setStats] = useState<VisitorStats | null>(null);
  const [liveVisitors, setLiveVisitors] = useState<VisitorActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [liveLoading, setLiveLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Product views state
  const [productViews, setProductViews] = useState<ProductViewRecord[]>([]);
  const [topProducts, setTopProducts] = useState<Array<{ product_name: string; view_count: number; unique_customers: number }>>([]);
  const [productLoading, setProductLoading] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productViewMode, setProductViewMode] = useState<'timeline' | 'top'>('top');
  const [selectedProductFilter, setSelectedProductFilter] = useState<string>('');

  const loadStats = useCallback(async () => {
    setLoading(true);
    const s = await fetchVisitorStats();
    setStats(s);
    setLastUpdated(new Date());
    setLoading(false);
  }, []);

  const loadLive = useCallback(async () => {
    setLiveLoading(true);
    const live = await fetchLiveVisitors();
    setLiveVisitors(live);
    setLiveLoading(false);
  }, []);

  const loadProductViews = useCallback(async () => {
    setProductLoading(true);
    const [views, top] = await Promise.all([fetchProductViews(30), fetchTopViewedProducts(30)]);
    setProductViews(views);
    setTopProducts(top);
    setProductLoading(false);
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (activeTab === 'live') {
      loadLive();
    } else if (activeTab === 'products') {
      loadProductViews();
    }
  }, [activeTab, loadLive, loadProductViews]);

  // Auto-refresh live tab every 30s
  useEffect(() => {
    if (!autoRefresh || activeTab !== 'live') return;
    const interval = setInterval(loadLive, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, activeTab, loadLive]);

  const uniqueCustomerVisitors = stats?.recentVisitors
    .filter(v => v.customer_phone)
    .reduce((acc, v) => {
      if (!acc.find(x => x.customer_phone === v.customer_phone)) acc.push(v);
      return acc;
    }, [] as VisitorActivity[])
    .slice(0, 30) ?? [];

  const liveUniqueSessions = new Set(liveVisitors.map(v => v.session_id)).size;
  const liveRegistered = liveVisitors.filter(v => v.customer_phone).length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center shadow-lg shadow-orange-500/25">
            <i className="ri-eye-line text-white text-lg"></i>
          </div>
          <div className="flex-1">
            <h2 className="text-gray-900 font-bold text-lg">Visitor & Customer Activity</h2>
            <p className="text-gray-400 text-xs">
              {lastUpdated ? `Updated: ${lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : 'Loading...'}
            </p>
          </div>
          <button
            onClick={() => { loadStats(); if (activeTab === 'live') loadLive(); }}
            disabled={loading}
            className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-all cursor-pointer"
          >
            <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          </button>
        </div>

        {/* Quick Stats Row */}
        {stats && !loading && (
          <div className="grid grid-cols-4 gap-3 mt-4">
            <div className="bg-orange-50 border border-orange-100 rounded-2xl p-3 text-center">
              <p className="text-orange-800 font-bold text-xl leading-none">{stats.todayVisits}</p>
              <p className="text-orange-600 text-[11px] mt-1">Today</p>
            </div>
            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3 text-center">
              <p className="text-slate-800 font-bold text-xl leading-none">{stats.uniqueSessions}</p>
              <p className="text-slate-500 text-[11px] mt-1">Sessions</p>
            </div>
            <div className="bg-purple-50 border border-purple-100 rounded-2xl p-3 text-center">
              <p className="text-purple-800 font-bold text-xl leading-none">{stats.registeredVisits}</p>
              <p className="text-purple-600 text-[11px] mt-1">Customers</p>
            </div>
            <div className="bg-green-50 border border-green-100 rounded-2xl p-3 text-center">
              <p className="text-green-800 font-bold text-xl leading-none">{stats.weekVisits}</p>
              <p className="text-green-600 text-[11px] mt-1">This Week</p>
            </div>
          </div>
        )}
      </div>

      {/* Tab Bar */}
      <div className="bg-white rounded-2xl border border-gray-100 p-2 flex gap-1.5 flex-wrap">
        {VTABS.map(tab => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                isActive ? `${tab.color} text-white` : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
              {tab.key === 'live' && (
                <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isActive ? 'bg-white/25 text-white' : 'bg-green-100 text-green-700'}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                  Live
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {activeTab === 'overview' && (
        <>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4 bg-white rounded-2xl border border-gray-100">
              <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-orange-500 animate-spin"></div>
              <p className="text-gray-500 text-sm">Loading analytics...</p>
            </div>
          ) : stats ? (
            <>
              {/* Device Breakdown */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <p className="text-gray-700 font-bold text-sm mb-4 flex items-center gap-2">
                  <i className="ri-device-line text-gray-500"></i>
                  Device Breakdown (Last 30 Days)
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-slate-50 rounded-xl p-4 text-center border border-slate-100">
                    <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center mx-auto mb-2">
                      <i className="ri-smartphone-line text-white"></i>
                    </div>
                    <p className="text-slate-800 font-bold text-lg">{stats.mobileVisits}</p>
                    <p className="text-slate-500 text-xs">Mobile</p>
                    <p className="text-slate-400 text-[11px]">
                      {stats.totalVisits > 0 ? Math.round((stats.mobileVisits / stats.totalVisits) * 100) : 0}%
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-xl p-4 text-center border border-orange-100">
                    <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center mx-auto mb-2">
                      <i className="ri-computer-line text-white"></i>
                    </div>
                    <p className="text-orange-800 font-bold text-lg">{stats.desktopVisits}</p>
                    <p className="text-orange-600 text-xs">Desktop</p>
                    <p className="text-orange-400 text-[11px]">
                      {stats.totalVisits > 0 ? Math.round((stats.desktopVisits / stats.totalVisits) * 100) : 0}%
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-4 text-center border border-purple-100">
                    <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center mx-auto mb-2">
                      <i className="ri-user-heart-line text-white"></i>
                    </div>
                    <p className="text-purple-800 font-bold text-lg">{stats.registeredVisits}</p>
                    <p className="text-purple-600 text-xs">Customers</p>
                    <p className="text-purple-400 text-[11px]">
                      {stats.totalVisits > 0 ? Math.round((stats.registeredVisits / stats.totalVisits) * 100) : 0}%
                    </p>
                  </div>
                </div>

                {/* Guest vs Registered bar */}
                {stats.totalVisits > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>Guests: {stats.guestVisits}</span>
                      <span>Customers: {stats.registeredVisits}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full"
                        style={{ width: `${(stats.registeredVisits / stats.totalVisits) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Today's Hourly Activity */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <p className="text-gray-700 font-bold text-sm mb-4 flex items-center gap-2">
                  <i className="ri-time-line text-gray-500"></i>
                  Today Ka Hourly Traffic
                </p>
                {stats.todayVisits === 0 ? (
                  <div className="text-center py-8 text-gray-400 text-sm">Aaj abhi koi visit nahi hua</div>
                ) : (
                  <HourlyChart data={stats.hourlyActivity} />
                )}
              </div>

              {/* Top Cities */}
              {stats.topCities.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-5">
                  <p className="text-gray-700 font-bold text-sm mb-4 flex items-center gap-2">
                    <i className="ri-map-pin-2-line text-gray-500"></i>
                    Top Cities
                  </p>
                  <div className="space-y-2">
                    {stats.topCities.map((c, i) => {
                      const maxCity = stats.topCities[0]?.count ?? 1;
                      return (
                        <div key={c.city} className="flex items-center gap-3">
                          <span className="w-5 text-xs text-gray-400 font-bold text-right shrink-0">#{i + 1}</span>
                          <div className="flex-1 flex items-center gap-2 min-w-0">
                            <i className="ri-map-pin-line text-orange-400 text-xs shrink-0"></i>
                            <span className="text-sm text-gray-800 font-medium truncate">{c.city}</span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-orange-400 rounded-full"
                                style={{ width: `${(c.count / maxCity) * 100}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-500 font-semibold w-8 text-right">{c.count}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Stats Summary */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <p className="text-gray-700 font-bold text-sm mb-4 flex items-center gap-2">
                  <i className="ri-bar-chart-grouped-line text-gray-500"></i>
                  30-Day Summary
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Total Visits', value: stats.totalVisits, icon: 'ri-eye-line', color: 'bg-slate-50 border-slate-100', text: 'text-slate-800' },
                    { label: 'Unique Sessions', value: stats.uniqueSessions, icon: 'ri-user-line', color: 'bg-orange-50 border-orange-100', text: 'text-orange-800' },
                    { label: 'Customer Visits', value: stats.registeredVisits, icon: 'ri-user-heart-line', color: 'bg-purple-50 border-purple-100', text: 'text-purple-800' },
                    { label: 'Guest Visits', value: stats.guestVisits, icon: 'ri-user-3-line', color: 'bg-gray-50 border-gray-100', text: 'text-gray-800' },
                  ].map(item => (
                    <div key={item.label} className={`${item.color} border rounded-xl p-4`}>
                      <div className="flex items-center gap-2 mb-2">
                        <i className={`${item.icon} text-gray-500 text-sm`}></i>
                        <p className="text-gray-500 text-xs">{item.label}</p>
                      </div>
                      <p className={`${item.text} font-bold text-2xl`}>{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-100 gap-3">
              <i className="ri-error-warning-line text-3xl text-gray-300"></i>
              <p className="text-gray-500 text-sm">Data load nahi hua. Refresh karo.</p>
            </div>
          )}
        </>
      )}

      {/* ── LIVE VISITORS TAB ── */}
      {activeTab === 'live' && (
        <div className="space-y-4">
          {/* Live header */}
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center shrink-0">
                  <i className="ri-pulse-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-green-800 font-bold">Live Visitors (Last 15 min)</p>
                  <p className="text-green-600 text-xs">{liveUniqueSessions} active sessions · {liveRegistered} customers</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <div
                    onClick={() => setAutoRefresh(p => !p)}
                    className={`relative w-9 h-5 rounded-full transition-all cursor-pointer ${autoRefresh ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${autoRefresh ? 'translate-x-4' : 'translate-x-0'}`}></span>
                  </div>
                  <span className="text-green-700 text-xs font-semibold whitespace-nowrap">Auto Refresh</span>
                </label>
                <button
                  onClick={loadLive}
                  disabled={liveLoading}
                  className="w-8 h-8 rounded-xl bg-green-200 flex items-center justify-center text-green-700 hover:bg-green-300 transition-all cursor-pointer"
                >
                  <i className={`ri-refresh-line text-sm ${liveLoading ? 'animate-spin' : ''}`}></i>
                </button>
              </div>
            </div>
          </div>

          {liveLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 bg-white rounded-2xl border border-gray-100">
              <div className="w-8 h-8 rounded-full border-3 border-gray-200 border-t-green-500 animate-spin"></div>
              <p className="text-gray-500 text-sm">Live data load ho raha hai...</p>
            </div>
          ) : liveVisitors.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-100 gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center">
                <i className="ri-ghost-line text-3xl text-gray-300"></i>
              </div>
              <div className="text-center">
                <p className="text-gray-600 font-semibold">Abhi koi visitor nahi</p>
                <p className="text-gray-400 text-sm mt-1">Last 15 minutes mein koi activity nahi</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {liveVisitors.map((v, i) => {
                const badge = actionBadge(v.action);
                return (
                  <div key={`${v.id}-${i}`} className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex items-center gap-3">
                    {/* Device */}
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                      <i className={`${deviceIcon(v.device_type)} text-gray-500 text-sm`}></i>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {v.customer_name ? (
                          <span className="text-sm font-bold text-gray-900">{v.customer_name}</span>
                        ) : (
                          <span className="text-sm font-semibold text-gray-400">Guest</span>
                        )}
                        {v.customer_phone && (
                          <span className="text-xs text-gray-400">{v.customer_phone}</span>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${badge.color}`}>{badge.label}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <i className={`${pageIcon(v.page)} text-gray-400 text-xs`}></i>
                        <span className="text-xs text-gray-500">{pageLabel(v.page)}</span>
                        {v.city && (
                          <span className="text-xs text-gray-400 flex items-center gap-0.5">
                            <i className="ri-map-pin-line"></i>{v.city}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Time */}
                    <div className="text-right shrink-0">
                      <p className="text-xs text-green-600 font-semibold">{timeAgo(v.created_at)}</p>
                      <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse mx-auto mt-1"></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── PAGE ANALYTICS TAB ── */}
      {activeTab === 'pages' && (
        <div className="space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 bg-white rounded-2xl border border-gray-100">
              <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-orange-500 animate-spin"></div>
              <p className="text-gray-500 text-sm">Loading...</p>
            </div>
          ) : stats && stats.topPages.length > 0 ? (
            <>
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <p className="text-gray-700 font-bold text-sm mb-4 flex items-center gap-2">
                  <i className="ri-pie-chart-2-line text-gray-500"></i>
                  Most Visited Pages (30 Days)
                </p>
                <div className="space-y-3">
                  {stats.topPages.map((p, i) => {
                    const maxP = stats.topPages[0]?.count ?? 1;
                    const pct = Math.round((p.count / stats.totalVisits) * 100);
                    return (
                      <div key={p.page} className="flex items-center gap-3">
                        <span className="w-5 text-xs text-gray-400 font-bold text-right shrink-0">#{i + 1}</span>
                        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
                          <i className={`${pageIcon(p.page)} text-orange-500 text-sm`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-sm text-gray-800 font-semibold">{pageLabel(p.page)}</p>
                            <div className="flex items-center gap-2 shrink-0 ml-2">
                              <span className="text-xs text-gray-400">{pct}%</span>
                              <span className="text-xs font-bold text-gray-700">{p.count}</span>
                            </div>
                          </div>
                          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full transition-all"
                              style={{ width: `${(p.count / maxP) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Page insights */}
              <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4">
                <p className="text-orange-800 font-bold text-sm mb-2 flex items-center gap-1">
                  <i className="ri-lightbulb-flash-line text-orange-500"></i>
                  Page Insights
                </p>
                <ul className="text-xs text-orange-700 space-y-1.5">
                  {stats.topPages[0] && (
                    <li className="flex items-start gap-1.5">
                      <i className="ri-check-line text-orange-500 shrink-0 mt-0.5"></i>
                      <span><strong>{pageLabel(stats.topPages[0].page)}</strong> sabse popular hai — {stats.topPages[0].count} visits</span>
                    </li>
                  )}
                  {stats.registeredVisits > 0 && (
                    <li className="flex items-start gap-1.5">
                      <i className="ri-check-line text-orange-500 shrink-0 mt-0.5"></i>
                      <span>{Math.round((stats.registeredVisits / stats.totalVisits) * 100)}% visits registered customers ki hain</span>
                    </li>
                  )}
                  {stats.mobileVisits > stats.desktopVisits && (
                    <li className="flex items-start gap-1.5">
                      <i className="ri-smartphone-line text-orange-500 shrink-0 mt-0.5"></i>
                      <span>Mobile traffic dominant hai — {Math.round((stats.mobileVisits / stats.totalVisits) * 100)}% users mobile se aate hain</span>
                    </li>
                  )}
                </ul>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-100 gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center">
                <i className="ri-pages-line text-3xl text-gray-300"></i>
              </div>
              <div className="text-center">
                <p className="text-gray-600 font-semibold">Abhi koi page data nahi</p>
                <p className="text-gray-400 text-sm mt-1">Jab customers website visit karenge, yahan dikhega</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── PRODUCT VIEWS TAB ── */}
      {activeTab === 'products' && (
        <div className="space-y-4">
          {/* Header */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-500 flex items-center justify-center shrink-0">
                <i className="ri-shopping-bag-3-line text-white text-lg"></i>
              </div>
              <div className="flex-1">
                <p className="text-rose-800 font-bold">Product View Tracking</p>
                <p className="text-rose-600 text-xs">Kis customer ne kis product ko kab dekha — last 30 days</p>
              </div>
              <button
                onClick={loadProductViews}
                disabled={productLoading}
                className="w-8 h-8 rounded-xl bg-rose-200 flex items-center justify-center text-rose-700 hover:bg-rose-300 transition-all cursor-pointer"
              >
                <i className={`ri-refresh-line text-sm ${productLoading ? 'animate-spin' : ''}`}></i>
              </button>
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="bg-white rounded-2xl border border-gray-100 p-2 flex gap-1.5">
            <button
              onClick={() => setProductViewMode('top')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${productViewMode === 'top' ? 'bg-rose-500 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
            >
              <i className="ri-bar-chart-2-line"></i>
              Top Products
            </button>
            <button
              onClick={() => setProductViewMode('timeline')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${productViewMode === 'timeline' ? 'bg-rose-500 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
            >
              <i className="ri-time-line"></i>
              Timeline (Date/Time)
            </button>
          </div>

          {productLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 bg-white rounded-2xl border border-gray-100">
              <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-rose-500 animate-spin"></div>
              <p className="text-gray-500 text-sm">Product views load ho raha hai...</p>
            </div>
          ) : (
            <>
              {/* ── TOP PRODUCTS VIEW ── */}
              {productViewMode === 'top' && (
                <div className="space-y-3">
                  {topProducts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-100 gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-rose-50 flex items-center justify-center">
                        <i className="ri-shopping-bag-3-line text-3xl text-rose-200"></i>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-600 font-semibold">Abhi koi product view data nahi</p>
                        <p className="text-gray-400 text-sm mt-1">Jab customers products dekhenge, yahan dikhega</p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-2xl border border-gray-100 p-5">
                      <p className="text-gray-700 font-bold text-sm mb-4 flex items-center gap-2">
                        <i className="ri-fire-line text-rose-500"></i>
                        Most Viewed Products (Last 30 Days)
                      </p>
                      <div className="space-y-3">
                        {topProducts.map((p, i) => {
                          const maxViews = topProducts[0]?.view_count ?? 1;
                          return (
                            <div key={p.product_name} className="flex items-center gap-3">
                              <span className="w-6 text-xs text-gray-400 font-bold text-right shrink-0">#{i + 1}</span>
                              <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center shrink-0">
                                <i className="ri-shopping-bag-line text-rose-500 text-sm"></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1 gap-2">
                                  <button
                                    onClick={() => { setSelectedProductFilter(p.product_name); setProductViewMode('timeline'); }}
                                    className="text-sm text-gray-800 font-semibold truncate hover:text-rose-600 transition-colors cursor-pointer text-left"
                                  >
                                    {p.product_name}
                                  </button>
                                  <div className="flex items-center gap-2 shrink-0">
                                    <span className="text-[10px] text-gray-400 bg-gray-50 border border-gray-100 rounded-full px-2 py-0.5 whitespace-nowrap">
                                      <i className="ri-user-line mr-0.5"></i>{p.unique_customers} customers
                                    </span>
                                    <span className="text-xs font-bold text-rose-600 whitespace-nowrap">{p.view_count} views</span>
                                  </div>
                                </div>
                                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-gradient-to-r from-rose-400 to-rose-500 rounded-full transition-all"
                                    style={{ width: `${(p.view_count / maxViews) * 100}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-gray-400 text-xs mt-4 flex items-center gap-1">
                        <i className="ri-information-line"></i>
                        Kisi product ka naam click karo — us product ke saare viewers dekhne ke liye
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* ── TIMELINE VIEW ── */}
              {productViewMode === 'timeline' && (
                <div className="space-y-3">
                  {/* Search + Filter */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-3 space-y-2">
                    <div className="relative">
                      <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                      <input
                        type="text"
                        value={productSearch}
                        onChange={e => setProductSearch(e.target.value)}
                        placeholder="Product name ya customer name search karo..."
                        className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-rose-300 focus:ring-2 focus:ring-rose-100 outline-none"
                      />
                    </div>
                    {selectedProductFilter && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Filter:</span>
                        <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-100 text-rose-700 text-xs font-bold">
                          <i className="ri-shopping-bag-line text-[10px]"></i>
                          {selectedProductFilter}
                          <button onClick={() => setSelectedProductFilter('')} className="ml-1 cursor-pointer hover:text-rose-900">
                            <i className="ri-close-line text-[10px]"></i>
                          </button>
                        </span>
                      </div>
                    )}
                    <p className="text-gray-400 text-xs px-1">
                      Total: {productViews.filter(v =>
                        (!selectedProductFilter || v.product_name === selectedProductFilter) &&
                        (!productSearch || v.product_name?.toLowerCase().includes(productSearch.toLowerCase()) || v.customer_name?.toLowerCase().includes(productSearch.toLowerCase()))
                      ).length} records
                    </p>
                  </div>

                  {/* Timeline List */}
                  {productViews.filter(v =>
                    (!selectedProductFilter || v.product_name === selectedProductFilter) &&
                    (!productSearch || v.product_name?.toLowerCase().includes(productSearch.toLowerCase()) || v.customer_name?.toLowerCase().includes(productSearch.toLowerCase()))
                  ).length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-14 bg-white rounded-2xl border border-gray-100 gap-3">
                      <i className="ri-search-line text-3xl text-gray-200"></i>
                      <p className="text-gray-500 text-sm">Koi record nahi mila</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {productViews
                        .filter(v =>
                          (!selectedProductFilter || v.product_name === selectedProductFilter) &&
                          (!productSearch || v.product_name?.toLowerCase().includes(productSearch.toLowerCase()) || v.customer_name?.toLowerCase().includes(productSearch.toLowerCase()))
                        )
                        .map((v, i) => {
                          const dt = new Date(v.created_at);
                          const dateStr = dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                          const timeStr = dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
                          return (
                            <div key={i} className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex items-start gap-3">
                              {/* Icon */}
                              <div className="w-9 h-9 rounded-lg bg-rose-50 flex items-center justify-center shrink-0 mt-0.5">
                                <i className="ri-shopping-bag-line text-rose-500 text-sm"></i>
                              </div>

                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-gray-900 truncate">{v.product_name}</p>
                                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                  {v.customer_name ? (
                                    <span className="flex items-center gap-1 text-xs text-gray-700 font-semibold">
                                      <i className="ri-user-line text-gray-400 text-[10px]"></i>
                                      {v.customer_name}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-gray-400 italic">Guest visitor</span>
                                  )}
                                  {v.customer_phone && (
                                    <span className="text-xs text-gray-400">{v.customer_phone}</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 mt-1 flex-wrap text-[11px] text-gray-400">
                                  {v.city && (
                                    <span className="flex items-center gap-0.5">
                                      <i className="ri-map-pin-line"></i>{v.city}
                                    </span>
                                  )}
                                  <span className="flex items-center gap-0.5">
                                    <i className={v.device_type === 'mobile' ? 'ri-smartphone-line' : 'ri-computer-line'}></i>
                                    {v.device_type}
                                  </span>
                                </div>
                              </div>

                              {/* Date Time */}
                              <div className="text-right shrink-0">
                                <p className="text-xs font-bold text-gray-700">{timeStr}</p>
                                <p className="text-[11px] text-gray-400 mt-0.5">{dateStr}</p>
                                {v.customer_phone && (
                                  <a
                                    href={`https://wa.me/91${v.customer_phone}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-green-50 border border-green-200 text-green-700 text-[10px] font-bold cursor-pointer hover:bg-green-100 transition-all whitespace-nowrap"
                                  >
                                    <i className="ri-whatsapp-fill text-[10px]"></i>
                                    Chat
                                  </a>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── CUSTOMER VISITS TAB ── */}
      {activeTab === 'customers' && (
        <div className="space-y-4">
          <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center shrink-0">
                <i className="ri-user-heart-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-purple-800 font-bold">Registered Customer Activity</p>
                <p className="text-purple-600 text-xs">Last 30 days mein logged-in customers ki activity</p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 bg-white rounded-2xl border border-gray-100">
              <div className="w-8 h-8 rounded-full border-4 border-gray-200 border-t-purple-500 animate-spin"></div>
              <p className="text-gray-500 text-sm">Loading...</p>
            </div>
          ) : uniqueCustomerVisitors.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-100 gap-4">
              <div className="w-14 h-14 rounded-2xl bg-purple-50 flex items-center justify-center">
                <i className="ri-user-heart-line text-3xl text-purple-200"></i>
              </div>
              <div className="text-center">
                <p className="text-gray-600 font-semibold">Koi customer activity nahi</p>
                <p className="text-gray-400 text-sm mt-1">Jab customers login karke browse karenge, yahan dikhega</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {uniqueCustomerVisitors.map((v, i) => {
                const badge = actionBadge(v.action);
                return (
                  <div key={`${v.id}-${i}`} className="bg-white rounded-2xl border border-gray-100 p-4">
                    <div className="flex items-start gap-3">
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                        {v.customer_name?.charAt(0).toUpperCase() ?? '?'}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-gray-900 text-sm">{v.customer_name}</p>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${badge.color}`}>{badge.label}</span>
                        </div>
                        <p className="text-gray-500 text-xs">{v.customer_phone}</p>
                        <div className="flex items-center gap-3 mt-1 flex-wrap text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <i className={pageIcon(v.page)}></i>
                            {pageLabel(v.page)}
                          </span>
                          {v.city && (
                            <span className="flex items-center gap-1">
                              <i className="ri-map-pin-line"></i>{v.city}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <i className={deviceIcon(v.device_type)}></i>
                            {v.device_type}
                          </span>
                        </div>
                      </div>

                      <div className="text-right shrink-0">
                        <p className="text-xs text-gray-400">{timeAgo(v.created_at)}</p>
                        <a
                          href={`https://wa.me/91${v.customer_phone}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1.5 flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-green-50 border border-green-200 text-green-700 text-xs font-bold cursor-pointer hover:bg-green-100 transition-all whitespace-nowrap"
                        >
                          <i className="ri-whatsapp-fill text-xs"></i>
                          Chat
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
